import { useCallback, useMemo, useState } from 'react'
import type { components } from '../../api/generated/schema'
import type { Notice } from '../../lib/envelope'
import { describeApiFailure } from '../../lib/failure'
import { comicApi, useComicMutation } from './mutations'

type Schema = components['schemas']

/**
 * Every comic mutation, in one place — the same bundling as characters, campaigns and
 * cases, so the page renders notices and failures once rather than once per card.
 *
 * `canEdit` is the server's `canManage`: members read a comic, the Game Master writes it.
 *
 * The `409` messages this surfaces are worth reading rather than generalizing: "already
 * has a front cover" and the unique-name-per-comic rule on scenes both arrive as a
 * conflict with the reason in `detail`, and `describeApiFailure` passes those through.
 */
export function useComicEditing(comicId: number, canEdit: boolean) {
  const [notices, setNotices] = useState<Notice[]>([])
  const [failure, setFailure] = useState<string | null>(null)

  const onEnvelope = useCallback((envelope: { notices: Notice[] }) => {
    setNotices(envelope.notices)
    setFailure(null)
  }, [])

  const onFailure = useCallback((error: unknown) => {
    setNotices([])
    setFailure(describeApiFailure(error, 'Only the Game Master can change this comic.'))
  }, [])

  const base = useMemo(() => ({ comicId, onEnvelope, onFailure }), [comicId, onEnvelope, onFailure])

  const update = useComicMutation<Schema['ComicRequest']>({
    ...base,
    request: (body) => comicApi.update(comicId, body),
  })

  const addScene = useComicMutation<Schema['ComicSceneRequest']>({
    ...base,
    request: (body) => comicApi.addScene(comicId, body),
  })

  const renameScene = useComicMutation<{ comicSceneId: number; name: string }>({
    ...base,
    request: ({ comicSceneId, name }) => comicApi.renameScene(comicId, comicSceneId, name),
  })

  const removeScene = useComicMutation<{ comicSceneId: number }>({
    ...base,
    request: ({ comicSceneId }) => comicApi.removeScene(comicId, comicSceneId),
  })

  const reorderScenes = useComicMutation<{ ids: number[] }>({
    ...base,
    request: ({ ids }) => comicApi.reorderScenes(comicId, ids),
  })

  const addPage = useComicMutation<Schema['ComicPageRequest']>({
    ...base,
    request: (body) => comicApi.addPage(comicId, body),
  })

  const updatePage = useComicMutation<{ pageId: number; body: Schema['UpdateComicPageRequest'] }>({
    ...base,
    request: ({ pageId, body }) => comicApi.updatePage(comicId, pageId, body),
  })

  const removePage = useComicMutation<{ pageId: number }>({
    ...base,
    request: ({ pageId }) => comicApi.removePage(comicId, pageId),
  })

  const reorderPages = useComicMutation<{ comicSceneId: number; ids: number[] }>({
    ...base,
    request: ({ comicSceneId, ids }) => comicApi.reorderPages(comicId, comicSceneId, ids),
  })

  const uploadImage = useComicMutation<{ pageId: number; file: File; title?: string }>({
    ...base,
    request: ({ pageId, file, title }) => comicApi.uploadImage(comicId, pageId, file, title),
  })

  const removeImage = useComicMutation<{ imageId: number }>({
    ...base,
    request: ({ imageId }) => comicApi.removeImage(comicId, imageId),
  })

  const all = [
    update,
    addScene,
    renameScene,
    removeScene,
    reorderScenes,
    addPage,
    updatePage,
    removePage,
    reorderPages,
    uploadImage,
    removeImage,
  ]

  return {
    comicId,
    canEdit,
    notices,
    failure,
    dismiss: useCallback(() => {
      setNotices([])
      setFailure(null)
    }, []),
    isPending: all.some((mutation) => mutation.isPending),
    update,
    addScene,
    renameScene,
    removeScene,
    reorderScenes,
    addPage,
    updatePage,
    removePage,
    reorderPages,
    uploadImage,
    removeImage,
  }
}

export type ComicEditing = ReturnType<typeof useComicEditing>
